import {
  chakra,
  forwardRef,
  omitThemingProps,
  useStyleConfig,
} from '@chakra-ui/react-v2';

import type { ButtonProps } from '../button';
import { useIconButtonGroup } from './IconButtonGroupContext';
import type { IconButtonSize } from './types';

export interface IconButtonProps extends ButtonProps {
  size?: IconButtonSize;
  isDisabled?: boolean;
  isIconColorReactive?: boolean;
}

export const IconButton = forwardRef<IconButtonProps, 'button'>(
  (props, ref) => {
    const group = useIconButtonGroup();
    const styles = useStyleConfig('IconButton', {
      ...group,
      ...props,
    });

    const iconButtonStyles = {
      display: 'inline-flex',
      appearance: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      position: 'relative',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      outline: 'none',
      ...styles,
    };

    const {
      isDisabled = group?.isDisabled,
      size = group?.size,
      children,
      className,
      as,
      type,
      isIconColorReactive: _,
      ...restProps
    } = { ...omitThemingProps(props), size: props.size };

    return (
      <chakra.button
        disabled={isDisabled}
        ref={ref}
        as={as}
        type={type}
        __css={iconButtonStyles}
        className={className}
        _notLast={
          group != null
            ? { marginInlineEnd: size ? '3px' : 'unset' }
            : undefined
        }
        {...restProps}
      >
        {children}
      </chakra.button>
    );
  },
);
IconButton.displayName = 'IconButton';
